import { useReadContracts } from 'wagmi';
import { erc20Abi } from 'viem';
import { QueryOptions } from 'utils/useQueryUtils';
import { balanceQuoterAbi, getOracleDeployment } from 'sfi-oracle-sdk';
import {
  normalizeQuoteDecimals,
  USD_QUOTE_DECIMALS,
} from '../utils/quoteDecimals';

export interface TokenInput {
  address: `0x${string}`;
  chainId: number;
}

export interface AssetsData {
  map: Record<
    number,
    Record<`0x${string}`, { balance: bigint; quote: bigint }>
  >;
  quotesTotal: bigint;
  decimals: number;
}

export const useUserTotalAssets = (
  params: {
    userAddress: `0x${string}`;
    tokens: TokenInput[];
  } & QueryOptions
) => {
  const { userAddress, tokens, query = {} } = params;

  // Group by chainId, skipping chains without an SDK deployment.
  const tokensByChain = tokens.reduce(
    (acc, token) => {
      if (!getOracleDeployment(token.chainId)) return acc;
      if (!acc[token.chainId]) acc[token.chainId] = [];
      acc[token.chainId].push(token.address);
      return acc;
    },
    {} as Record<number, `0x${string}`[]>
  );

  const chainIds = Object.keys(tokensByChain).map(Number);

  const contracts = chainIds.flatMap((chainId) => {
    const deployment = getOracleDeployment(chainId)!;

    return [
      {
        abi: balanceQuoterAbi,
        address: deployment.balanceQuoter,
        functionName: 'quoteBalancesFor' as const,
        chainId,
        args: [
          userAddress,
          deployment.referenceAsset,
          tokensByChain[chainId],
        ] as const,
      },
      {
        abi: erc20Abi,
        address: deployment.referenceAsset,
        functionName: 'decimals' as const,
        chainId,
      },
    ];
  });

  const queryOptions = {
    refetchInterval: 15000,
    staleTime: 15000,
    ...query,
    enabled: query.enabled !== false && !!userAddress && tokens.length > 0,
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore wait for strictmode fix
  return useReadContracts({
    contracts,
    query: {
      ...queryOptions,
      select: (results): AssetsData => {
        const map: Record<
          number,
          Record<`0x${string}`, { balance: bigint; quote: bigint }>
        > = {};
        let quotesTotal = BigInt(0);

        chainIds.forEach((chainId, i) => {
          const result = results[i * 2];
          const decimalsResult = results[i * 2 + 1];
          if (
            result.status !== 'success' ||
            !result.result ||
            decimalsResult.status !== 'success'
          )
            return;

          const [balances, quotes] = result.result as readonly [
            readonly bigint[],
            readonly bigint[],
          ];
          const quoteDecimals = Number(decimalsResult.result);
          const addresses = tokensByChain[chainId];

          map[chainId] = {};
          addresses.forEach((address, j) => {
            const normalizedQuote = normalizeQuoteDecimals(
              quotes[j],
              quoteDecimals
            );
            map[chainId][address] = {
              balance: balances[j],
              quote: normalizedQuote,
            };
            quotesTotal += normalizedQuote;
          });
        });

        return { map, quotesTotal, decimals: USD_QUOTE_DECIMALS };
      },
    },
  });
};
